import React, { useEffect, useRef } from 'react';
import XappexLogo from 'assets/images/xappex-logo.svg';
import SalesforceLogoUrl from 'assets/images/salesforce-logo-mini.svg';
import HubspotLogoUrl from 'assets/images/hubspot-logo-mini.svg';
import { useStyles } from "./SignIn.styles";
import UserStore from "@v2/stores/userStore";
import { AccountList } from "@v2/views/SignIn/AccountList/AccountList";
import { runAction, runCancelableAction } from "src/services/utils";
import { CancelToken } from "shared/utils/cancelToken";
import { AccountType } from "shared/types/flowstep";
import { Account, SalesforceMode } from "shared/types/authorization";
import { inject, observer } from "mobx-react";
import { useHistory } from "react-router-dom";
import { ProviderButtonMenu } from "@v2/views/SignIn/ProviderButtonMenu/ProviderButtonMenu";
import office from 'src/services/office';
import { requestStore } from 'src/stores';

interface SignInProps {
  userStore: UserStore;
}

const SignIn: React.FC<SignInProps> = (props) => {
  const { userStore } = props;
  const cancelTokenRef = useRef<CancelToken | null>(null);
  const styles = useStyles();
  const history = useHistory();

  useEffect(() => () => {
    cancelTokenRef.current?.cancel();
  }, []);

  const handleProviderClick = async (
    accountType: Exclude<AccountType, 'salesforce-gc'>,
    sfMode: SalesforceMode = 'production',
    useBrowser = false
  ) => {
    await runCancelableAction(async (cancelToken: CancelToken) => {
      requestStore.setText('Connecting to your account...');
      await userStore.signInOrUp({ username: '', accountType, sfMode }, useBrowser, cancelToken);
      history.goBack();
    });
  }

  const handleAccountClick = async (account: Account) => {
    await runAction(async () => {
      cancelTokenRef.current?.cancel();
      cancelTokenRef.current = CancelToken.source();
      await userStore.signInOrUp({
        username: account.username,
        accountType: account.loginData.accountType,
        sfMode: account.loginData.sfMode
      }, false, cancelTokenRef.current);
    });
    history.goBack();
  }

  const handleRemoveAccountClick = async (account: Account) => {
    await userStore.forgetAccount(account.loginData);
  }

  const isExcelOnline = (office.platformType as unknown as Office.PlatformType === Office.PlatformType.OfficeOnline);

  return (
    <div className={styles.signInView}>
      <div className={styles.topLogo}>
        <img src={XappexLogo} alt="Xappex" />
      </div>
      <div className={styles.topText}>
        {!userStore.accounts?.length && (
          <div style={{ textAlign: 'center' }}>Connect your data provider</div>
        )}
        {!!userStore.accounts?.length && (
          <div style={{ textAlign: 'left' }}>Choose the recent login or connect your data provider</div>
        )}
      </div>
      {userStore.accounts?.length > 0 && (
        <div className={styles.accounts}>
          <AccountList
            accounts={userStore.accounts}
            currentAccount={userStore.user}
            onAccountClick={(account) => handleAccountClick(account)}
            onRemoveAccountClick={(account) => handleRemoveAccountClick(account)}
          />
          <div className='divider' />
        </div>
      )}
      <div className={styles.providers}>
        <div
          className={styles.providerButton}
          onClick={() => handleProviderClick(AccountType.SF)}
        >
          <img className='icon' src={SalesforceLogoUrl} alt="SF" width={24} height={24} />
          <div className='text'>
            Connect to Salesforce
          </div>
          <ProviderButtonMenu>
            {!isExcelOnline &&
              <div
                className={styles.menuItem}
                onClick={() => handleProviderClick(AccountType.SF, 'production', true)}
              >
                Connect using browser
              </div>
            }
            <div
              className={styles.menuItem}
              onClick={() => handleProviderClick(AccountType.SF, 'sandbox')}
            >
              Connect to Sandbox
            </div>
            {!isExcelOnline &&
              <div
                className={styles.menuItem}
                onClick={() => handleProviderClick(AccountType.SF, 'sandbox', true)}
              >
                Connect to Sandbox using browser
              </div>
            }
          </ProviderButtonMenu>
        </div>
        <div
          className={styles.providerButton}
          onClick={() => handleProviderClick(AccountType.HS)}
        >
          <img className='icon' src={HubspotLogoUrl} alt="HS" width={24} height={24} />
          <div className='text'>
            Connect to Hubspot
          </div>
          <ProviderButtonMenu>
            {!isExcelOnline &&
              <div
                className={styles.menuItem}
                onClick={() => handleProviderClick(AccountType.HS, 'production', true)}
              >
                Connect using browser
              </div>
            }
          </ProviderButtonMenu>
        </div>
      </div>
    </div>
  );
};

export default inject('userStore')(observer(SignIn));
